/**
 * useWorkspaceFiles — fetch + cache workspace file paths for autocomplete
 *
 * Lazy-loaded: only fetches when ensureLoaded() is called.
 * Re-fetches after 30s stale window.
 */
import { useState, useRef, useCallback } from 'react'
import { request } from '@papan/api-client'

const STALE_MS = 30_000

export function useWorkspaceFiles(workspaceId: string) {
  const [files, setFiles] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const lastFetchRef = useRef<number>(0)
  const fetchingRef = useRef(false)
  const loadedWorkspaceRef = useRef<string>('')

  const ensureLoaded = useCallback(async () => {
    const now = Date.now()
    const isStale = now - lastFetchRef.current > STALE_MS
    const isDifferentWorkspace = loadedWorkspaceRef.current !== workspaceId

    if (fetchingRef.current || (!isStale && !isDifferentWorkspace)) return

    fetchingRef.current = true
    setLoading(true)
    try {
      const data = await request<{ files: string[] }>(
        `/api/agent/files?workspaceId=${encodeURIComponent(workspaceId)}`,
      )
      setFiles(data.files)
      lastFetchRef.current = Date.now()
      loadedWorkspaceRef.current = workspaceId
    } catch {
      // Non-fatal — autocomplete just won't populate
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [workspaceId])

  return { files, loading, ensureLoaded }
}
